import { NavLink, useNavigate } from 'react-router-dom';
import { ReactNode, useState, useEffect, useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { api } from '../services/api';
import {
  DashboardIcon, ServicesIcon, OrdersIcon, InvoicesIcon,
  PromotionsIcon, ProfileIcon, SettingsIcon, NotificationIcon, LogoutIcon, ReviewsIcon
} from './icons/SidebarIcons';
import NotificationBell from './notifications/NotificationBell';
import NotificationPanel from './notifications/NotificationPanel';
import { AdminActionFeedProvider, useAdminActionFeedContext } from '../context/AdminActionFeedContext';
import { DEALER_ROLES } from '../hooks/useAuth';
import LanguageSwitcher from './LanguageSwitcher';

interface LayoutProps {
  children: ReactNode;
  auth: {
    isAuthenticated: boolean;
    token: string | null;
    user: { name: string; email: string; roles: string[] } | null;
    logout: () => void;
  };
  garageStatus?: string | null;
  garageName?: string | null;
}

// Orders is an early-preview surface (F1 #162 partial delivery) — keep it
// disabled in prod until the full OMS lands. Stage/dev/local stay enabled
// so the team can review on stage. The check uses VITE_API_URL which is
const navItems = [
  { to: '/dashboard', labelKey: 'nav.dashboard', label: 'Dashboard', Icon: DashboardIcon },
  { to: '/services', labelKey: 'nav.myServices', label: 'Services', Icon: ServicesIcon },
  { to: '/orders', labelKey: 'nav.orders', label: 'Orders', Icon: OrdersIcon },
  { to: '/reviews', labelKey: 'nav.reviews', label: 'Reviews', Icon: ReviewsIcon },
  { to: '/earnings', labelKey: 'nav.earnings', label: 'Earnings', Icon: InvoicesIcon },
  { to: '/promotions', labelKey: 'nav.promotions', label: 'Promotions', Icon: PromotionsIcon },
  { to: '/profile', labelKey: 'nav.profile', label: 'Profile', Icon: ProfileIcon },
  { to: '/settings', labelKey: 'nav.settings', label: 'Settings', Icon: SettingsIcon },
  { to: '/notifications', labelKey: 'nav.notifications', label: 'Notifications', Icon: NotificationIcon, disabled: true },
];

// Spec 085 (§1, §4): dealer users get the dealer portal's five items instead of the garage ones.
const dealerNavItems = [
  { to: '/dealer/dashboard', labelKey: 'dealer.nav.dashboard', label: 'Dashboard', Icon: DashboardIcon },
  { to: '/dealer/credit', labelKey: 'dealer.nav.credit', label: 'Credit', Icon: InvoicesIcon },
  { to: '/dealer/bookings', labelKey: 'dealer.nav.bookings', label: 'Bookings', Icon: OrdersIcon },
  { to: '/dealer/documents', labelKey: 'dealer.nav.documents', label: 'Documents', Icon: ServicesIcon },
  { to: '/dealer/settings', labelKey: 'dealer.nav.settings', label: 'Settings', Icon: SettingsIcon },
];

function isDealerUser(roles: string[] | undefined): boolean {
  return !!roles && roles.some(r => DEALER_ROLES.includes(r));
}

function Layout(props: LayoutProps) {
  // The admin-action feed polls garage endpoints; a dealer has none.
  const feedToken = isDealerUser(props.auth.user?.roles) ? null : props.auth.token;
  return (
    <AdminActionFeedProvider token={feedToken}>
      <LayoutInner {...props} />
    </AdminActionFeedProvider>
  );
}

function LayoutInner({ children, auth, garageName: garageNameProp }: LayoutProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { unreadCount, clearLocal } = useAdminActionFeedContext();

  const onServicesNavClick = async () => {
    if (unreadCount === 0 || !auth.token) return;
    clearLocal();
    try {
      await api.markAdminActionsRead(auth.token);
    } catch {
      // Best-effort. The next 60s poll will re-check unread state.
    }
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [fetchedGarageName, setFetchedGarageName] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const userName = auth.user?.name || '';
  const initial = userName ? userName.trim().charAt(0).toUpperCase() : '?';
  const garageName = garageNameProp ?? fetchedGarageName;
  const isDealer = isDealerUser(auth.user?.roles);
  const items: Array<{ to: string; labelKey: string; label: string; Icon: typeof DashboardIcon; disabled?: boolean }> =
    isDealer ? dealerNavItems : navItems;

  useEffect(() => {
    // Fetch garage name once, only if not provided by parent and we have a token (never for a dealer)
    if (isDealer || garageNameProp || fetchedGarageName || !auth.token) return;
    let cancelled = false;
    api.getMyGarage(auth.token)
      .then(g => { if (!cancelled) setFetchedGarageName(g?.name || null); })
      .catch(() => { /* user may not have a garage yet (onboarding) — ignore */ });
    return () => { cancelled = true; };
  }, [auth.token, garageNameProp, fetchedGarageName, isDealer]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  return (
    <div className="figma-layout">
      <aside className="figma-sidebar">
        {/* Brand */}
        <div className="figma-sidebar-brand">
          <div className="figma-sidebar-logo">
            <svg width="38" height="38" viewBox="0 0 50 50" fill="none">
              <rect width="50" height="50" rx="8" fill="#4f46e5"/>
              <text x="25" y="33" textAnchor="middle" fill="#ffffff" fontSize="24" fontWeight="700" fontFamily="inherit">G</text>
            </svg>
          </div>
          <div className="figma-sidebar-brand-text">
            <span className="figma-sidebar-brand-name">Garagat.ai</span>
            <span className="figma-sidebar-brand-tagline">{t(isDealer ? 'dealer.layout.tagline' : 'layout.tagline')}</span>
          </div>
        </div>

        {garageName && (
          <>
            <div className="figma-sidebar-divider" />
            <div className="figma-sidebar-context" ref={menuRef}>
              <div className="figma-sidebar-garage-name" title={garageName}>{garageName}</div>
              <button
                className={`figma-sidebar-user ${menuOpen ? 'open' : ''}`}
                onClick={() => setMenuOpen(o => !o)}
                aria-haspopup="true"
                aria-expanded={menuOpen}
              >
                <div className="figma-sidebar-user-avatar">{initial}</div>
                <span className="figma-sidebar-user-name">{userName || t('layout.user')}</span>
                <span className="figma-sidebar-user-caret">⌄</span>
              </button>

              {menuOpen && (
                <div className="figma-sidebar-user-menu" role="menu">
                  {!isDealer && (
                    <button
                      className="figma-sidebar-user-menu-item"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); navigate('/me'); }}
                    >
                      <ProfileIcon size={18} />
                      <span>{t('nav.myProfile') || 'My Profile'}</span>
                    </button>
                  )}
                  <button
                    className="figma-sidebar-user-menu-item figma-sidebar-user-menu-logout"
                    role="menuitem"
                    onClick={() => { setMenuOpen(false); auth.logout(); }}
                  >
                    <LogoutIcon size={18} />
                    <span>{t('nav.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        <div className="figma-sidebar-divider" />

        {/* Navigation */}
        <nav className="figma-sidebar-nav">
          {items.map((item) =>
            item.disabled ? (
              <div key={item.to} className="figma-nav-item disabled">
                <item.Icon size={24} />
                <span>{t(item.labelKey) === item.labelKey ? item.label : t(item.labelKey)}</span>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `figma-nav-item ${isActive ? 'active' : ''}`}
                onClick={item.to === '/services' ? onServicesNavClick : undefined}
              >
                <item.Icon size={24} />
                <span>{t(item.labelKey) === item.labelKey ? item.label : t(item.labelKey)}</span>
                {item.to === '/services' && unreadCount > 0 && (
                  <span className="figma-nav-item__dot" aria-label="Has unread updates" />
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className="figma-sidebar-spacer" />

        {/* Fallback logout (for users without garage context, e.g. mid-onboarding) */}
        {!garageName && (
          <>
            <div className="figma-sidebar-divider" />
            <button className="figma-nav-item figma-logout" onClick={auth.logout}>
              <LogoutIcon size={24} />
              <span>{t('nav.logout')}</span>
            </button>
          </>
        )}
      </aside>

      <div className="figma-main">
        <div className="figma-topbar">
          <div className="figma-topbar__actions">
            {isDealer
              ? <LanguageSwitcher variant="inline" />
              : <NotificationBell token={auth.token} onClick={() => setNotifOpen(true)} />}
          </div>
        </div>
        <main className="figma-content">{children}</main>
        {!isDealer && (
          <NotificationPanel
            token={auth.token}
            isOpen={notifOpen}
            onClose={() => setNotifOpen(false)}
            onMarkRead={() => setNotifOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

export default Layout;
